import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface Repository {
  id: string;
  name: string;
  full_name: string;
  url: string;
  platform_type: 'github' | 'gitlab' | 'azure_devops';
  primary_language: string | null;
  size_bytes: number | null;
  default_branch: string;
  last_commit_date: string | null;
  is_private: boolean;
  is_fork: boolean;
  analysis_enabled: boolean;
  has_github_token: boolean;
  last_analyzed_at: string | null;
  latest_technical_debt_hours: number;
  latest_test_coverage_percentage: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  /** Set when the repo no longer exists on the platform; its history is kept. */
  archived_at: string | null;
  archived_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

interface RepositoriesResponse {
  repositories: Repository[];
  pagination: PaginationInfo;
}

interface RepositoriesFilter {
  page?: number;
  page_size?: number;
  sort_by?: string;
  order?: 'asc' | 'desc';
  platform_type?: string;
  analysis_enabled?: string;
  search?: string;
  /** 'true' lists archived repositories instead of active ones. */
  archived?: string;
}

export const useRepositories = (filter: RepositoriesFilter = {}) => {
  const { user, currentOrganization } = useAuth();

  const {
    page = 1,
    page_size = 20,
    sort_by = 'last_commit_date',
    order = 'desc',
    platform_type,
    analysis_enabled,
    search,
    archived,
  } = filter;

  return useQuery<RepositoriesResponse>({
    queryKey: ['repositories', page, page_size, sort_by, order, platform_type, analysis_enabled, search, archived, currentOrganization?.id],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(page_size),
        sort_by,
        order,
      });

      if (platform_type) {
        params.append('platform_type', platform_type);
      }
      if (analysis_enabled) {
        params.append('analysis_enabled', analysis_enabled);
      }
      if (search) {
        params.append('search', search);
      }
      if (archived) {
        params.append('archived', archived);
      }

      const response = await api.get<RepositoriesResponse>(`/api/repositories?${params.toString()}`);
      return response.data;
    },
    enabled: !!user && !!currentOrganization,
    placeholderData: keepPreviousData,
  });
};

export const useRepository = (id: string | undefined) => {
  const { user, currentOrganization } = useAuth();

  return useQuery<Repository>({
    queryKey: ['repository', id, currentOrganization?.id],
    queryFn: async () => {
      const response = await api.get<Repository>(`/api/repositories/${id}`);
      return response.data;
    },
    enabled: !!user && !!currentOrganization && !!id,
  });
};

export const useToggleRepositoryAnalysis = () => {
  return {
    toggleAnalysis: async (repoId: string, enabled: boolean) => {
      await api.put(`/api/repositories/${repoId}/analysis`, { enabled });
    },
  };
};

export type { Repository, PaginationInfo, RepositoriesResponse, RepositoriesFilter };
